import React, { useState, useEffect, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { IconButton, Tooltip, Autocomplete } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  StyledButton,
  StyledCancelButton,
  ModernHeader,
  inputStyle,
  StyledAgGridContainer,
} from "components/StyledComponents/styledComponents.js";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Cookie from "js-cookie";
import URLs from "constants/urls";
import Grid from "@mui/material/Grid"; // Grid version 2
import { CustomPaper } from "components/Styles/globalStyles";
import PropTypes from "prop-types";

const formatCurrencyINR = (value) => {
  return value?.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  });
};

const GstTdsGrid = ({ setAlert, setLoading, loading }) => {
  const MInput = inputStyle();
  const [rowData, setRowData] = useState([]);
  const [rowDsaData, setRowDsaData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filteredDsaData, setFilteredDsaData] = useState([]);
  const [footerData, setFooterData] = useState([]);
  const [footerDsaData, setFooterDsaData] = useState([]);
  const [gstStatus, setGstStatus] = useState("All");
  const [gstStatusDsa, setGstStatusDsa] = useState("All");
  const gstStatusOptions = ["All", "Received", "Pending"];
  const gstStatusOptionsDsa = ["All", "Received", "Pending"];
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const formatDate = (date) => date.toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(formatDate(firstDayOfMonth));
  const [toDate, setToDate] = useState(formatDate(lastDayOfMonth));
  const [fromDateDsa, setFromDateDsa] = useState(formatDate(firstDayOfMonth));
  const [toDateDsa, setToDateDsa] = useState(formatDate(lastDayOfMonth));
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [confirmDsaDialogOpen, setConfirmDsaDialogOpen] = useState(false);
  const [selectedDsaReceipt, setSelectedDsaReceipt] = useState(null);
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().split("T")[0]);
  const jwt_token = Cookie.get("jwt_token");

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${URLs.fetchtingKhInvoices}/${fromDate}/${toDate}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (response.status === 204) {
        setRowData([]);
        setFilteredData([]);
        //setAlert("No invoices found for the selected date range.", "info");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch invoices");
      }

      const data = await response.json();
      setRowData(data);
      setFilteredData(data);
      return data;
    } catch (error) {
      console.error("Error:", error);
      setAlert("Failed to load invoices", "error");
    } finally {
      setLoading(false);
    }
  };
  const handleDateChange = (event) => {
    setReceiptDate(event.target.value); // Will be in YYYY-MM-DD format
  };

  const fetchDsaInvoices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${URLs.fetchtingDsaInvoices}/${fromDateDsa}/${toDateDsa}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (response.status === 204) {
        setRowDsaData([]);
        setFilteredDsaData([]);
        //setAlert("No DSA invoices found for the selected date range.", "info");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch invoices");
      }

      const data = await response.json();
      setRowDsaData(data);
      setFilteredDsaData(data);
      return data;
    } catch (error) {
      console.error("Error:", error);
      setAlert("Failed to load invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchDsaInvoices();
  }, []);

  const handleMarkGstPaid = useCallback(async (receiptNo) => {
    setLoading(true);
    if (!receiptDate) {
      setAlert("Please Select Amount Reciept Date", "error");
    }
    try {
      const url = `${URLs.updateKhReceiptBy}/${receiptNo}/Y/${receiptDate}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to mark GST as paid");
      }

      const data = await response.text();
      setAlert(data, "success");
      fetchInvoices();
    } catch (error) {
      setAlert("Failed to mark GST as paid", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMarkDsaGstPaid = useCallback(async (receiptNo) => {
    setLoading(true);
    if (!receiptDate) {
      setAlert("Please Select Amount Reciept Date", "error");
    }
    try {
      const url = `${URLs.updateDSAreceiptby}/${receiptNo}/Y/${receiptDate}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to mark GST as paid");
      }

      const data = await response.text();
      setAlert(data, "success");
      fetchDsaInvoices();
    } catch (error) {
      setAlert("Failed to mark GST as paid", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleViewInvoicePdf = useCallback(async (invoiceNo) => {
    setLoading(true);
    try {
      if (!invoiceNo) {
        throw new Error("Invoice number is missing");
      }

      const response = await fetch(`${URLs.khInvoiceViewUrl}/${invoiceNo}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch invoice");
      }

      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);

      // Open in a new tab
      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
    setLoading(false);
  }, []);

  const handleViewDsaInvoicePdf = useCallback(async (invoiceNo) => {
    setLoading(true);
    try {
      if (!invoiceNo) {
        throw new Error("Invoice number is missing");
      }

      const response = await fetch(`${URLs.headOfficeInvoiceView}/${invoiceNo}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch invoice");
      }

      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);

      // Open in a new tab
      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
    setLoading(false);
  }, []);
  function formatDateToDDMonYYYY(dateString) {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const date = new Date(dateString);
    if (isNaN(date)) return ""; // return empty if invalid date

    const day = String(date.getDate()).padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  const columnDefs = [
    {
      headerName: "Invoice Serial No",
      field: "invoiceDisplayNumber",
      cellRenderer: (params) => {
        const isFooter = params.data?.isFooter;

        if (!isFooter) {
          // Regular row: show icon button
          return (
            <>
              <Tooltip title="View Invoice PDF">
                <IconButton
                  onClick={() => handleViewInvoicePdf(params.data?.invoiceNo)}
                  size="small"
                  color="primary"
                  sx={{ mr: 1 }}
                >
                  <VisibilityIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
              {params.value}
            </>
          );
        } else {
          // Footer row: show the plain invoiceDisplayNumber
          return <span>{params.value}</span>;
        }
      },
      pinned: "left",
      width: 200,
    },
    {
      headerName: "Date",
      field: "invoiceDate",
      width: 120,
      valueFormatter: (params) => {
        const isFooter = params.data?.isFooter;
        return !isFooter ? formatDateToDDMonYYYY(params.value) : "";
      },
    },
    {
      headerName: "Sub Total",
      field: "subTotal",
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 130,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Total GST",
      valueGetter: (params) =>
        (params.data?.cgst || 0) + (params.data?.sgst || 0) + (params.data?.igst || 0),
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 130,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Invoice Amount",
      field: "invoiceAmount",
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 150,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Net Receipt",
      field: "netReceipt",
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 150,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "TDS",
      field: "tds",
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 120,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "GST Status",
      field: "gstStatus",
      width: 140,
      cellStyle: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      cellRenderer: (params) => {
        const status = params.value;
        const isPaid = status?.toLowerCase() === "received" || status?.toLowerCase() === "paid";
        const isFooter = params.data?.isFooter;

        const style = {
          margin: "0 auto",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          padding: "4px 10px",
          height: "30px",
          borderRadius: "10px",
          fontSize: "0.75rem",
          fontWeight: 600,
          color: isPaid ? "#155724" : "#856404",
          backgroundColor: isPaid ? "#d4edda" : "#fff3cd",
          border: `1px solid ${isPaid ? "#c3e6cb" : "#ffeeba"}`,
          textAlign: "center",
          minWidth: "90px",
        };
        if (!isFooter) {
          return <span style={style}>{isPaid ? "Received" : "Pending"}</span>;
        } else {
          return "";
        }
      },
    },
    {
      headerName: "Action",
      field: "action",
      cellRenderer: (params) => {
        const isFooter = params.data?.isFooter;
        const isGstPaid = params.data.gstStatus?.toLowerCase() == "received";

        if (!isFooter && !isGstPaid) {
          return (
            <Tooltip title="Mark as GST Paid">
              <IconButton
                onClick={() => {
                  setSelectedReceipt(params.data.receiptID);
                  setConfirmDialogOpen(true);
                }}
                color="success"
                size="small"
              >
                <CheckCircleIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          );
        } else {
          return null;
        }
      },
      maxWidth: 70,
      filter: false,
      sortable: false,
    },
  ];

  const columnDsaDefs = [
    {
      headerName: "Invoice Serial No",
      field: "invoiceDisplayNumber",
      cellRenderer: (params) => {
        const isSubtotal = params.data?.rowType === "Subtotal";

        if (isSubtotal) return <span>{`${params.data?.dsaName} - Total`}</span>;

        return (
          <>
            <Tooltip title="View Invoice PDF">
              <IconButton
                onClick={() => handleViewDsaInvoicePdf(params.data?.invoiceNo)}
                size="small"
                color="primary"
                sx={{ mr: 1 }}
              >
                <VisibilityIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
            {params.value}
          </>
        );
      },
      pinned: "left",
      width: 200,
    },
    {
      headerName: "Date",
      field: "invoiceDate",
      width: 120,
      valueFormatter: (params) => {
        const isFooter = params.data?.rowType === "Subtotal";
        return !isFooter ? formatDateToDDMonYYYY(params.value) : "";
      },
    },
    {
      headerName: "Sub Total",
      field: "subTotal",
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 130,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Total GST",
      valueGetter: (params) =>
        (params.data?.cgst || 0) + (params.data?.sgst || 0) + (params.data?.igst || 0),
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 130,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Invoice Amount",
      field: "invoiceAmount",
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 150,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "Net Receipt",
      field: "netReceipt",
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 150,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "TDS",
      field: "tds",
      valueFormatter: (params) => formatCurrencyINR(params.value),
      width: 120,
      cellStyle: { textAlign: "right", fontWeight: "500" },
    },
    {
      headerName: "GST Status",
      field: "gstStatus",
      width: 140,
      cellStyle: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      cellRenderer: (params) => {
        const status = params.value;
        const isSubtotal = params.data?.rowType === "Subtotal";

        if (isSubtotal) return null;

        const isPaid = status?.toLowerCase() === "received" || status?.toLowerCase() === "paid";
        const style = {
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4px 10px",
          height: "30px",
          borderRadius: "10px",
          fontSize: "0.75rem",
          fontWeight: 600,
          color: isPaid ? "#155724" : "#856404",
          backgroundColor: isPaid ? "#d4edda" : "#fff3cd",
          border: `1px solid ${isPaid ? "#c3e6cb" : "#ffeeba"}`,
          minWidth: "90px",
        };

        return <span style={style}>{isPaid ? "Received" : "Pending"}</span>;
      },
    },
    {
      headerName: "Action",
      field: "action",
      cellRenderer: (params) => {
        const isSubtotal = params.data?.rowType === "Subtotal";
        const isPaid = params.data?.gstStatus?.toLowerCase() === "received";

        if (isSubtotal || isPaid) return null;

        return (
          <Tooltip title="Mark as GST Paid">
            <IconButton
              onClick={() => {
                setSelectedDsaReceipt(params.data.receiptID);
                setConfirmDsaDialogOpen(true);
              }}
              color="success"
              size="small"
            >
              <CheckCircleIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        );
      },
      maxWidth: 70,
      filter: false,
      sortable: false,
    },
  ];

  useEffect(() => {
    const filtered = rowData.filter((item) => {
      if (gstStatus === "All") return true;
      if (gstStatus === "Received") return item.gstStatus === "Received";
      return item.gstStatus !== "Received";
    });

    setFilteredData(filtered);

    const totals = {
      invoiceDisplayNumber: "Total", // Key to match column field if needed
      subTotal: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      invoiceAmount: 0,
      netReceipt: 0,
      tds: 0,
      isFooter: true,
    };

    filtered.forEach((row) => {
      totals.subTotal += row.subTotal || 0;
      totals.cgst += row.cgst || 0;
      totals.sgst += row.sgst || 0;
      totals.igst += row.igst || 0;
      totals.invoiceAmount += row.invoiceAmount || 0;
      totals.netReceipt += row.netReceipt || 0;
      totals.tds += row.tds || 0;
    });

    setFooterData([totals]);
  }, [rowData, gstStatus]);

  const ConfirmDialog = ({
    open,
    onClose,
    onConfirm,
    title = "Are you sure?",
    content = "",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    confirmColor = "success",
  }) => {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {content}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Receipt Date */}
            <Grid item xs={12} sm={12}>
              <TextField
                label="Select Date"
                type="date"
                value={receiptDate} // Controlled input
                onChange={handleDateChange} // Updates state
                fullWidth
                sx={{ ...MInput.b }}
                InputLabelProps={{
                  shrink: true,
                  sx: { color: MInput.a },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <StyledCancelButton onClick={onClose} color="inherit">
            {cancelLabel}
          </StyledCancelButton>
          <StyledButton onClick={onConfirm} variant="contained" color={confirmColor}>
            {confirmLabel}
          </StyledButton>
        </DialogActions>
      </Dialog>
    );
  };

  ConfirmDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,

    title: PropTypes.string,
    content: PropTypes.node,
    confirmLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    confirmColor: PropTypes.string,
  };

  return (
    <>
      <ModernHeader label="KH GST / TDS According to Invoiced Date" />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, mt: 2, alignItems: "center" }}
      >
        <TextField
          label="From Date"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          sx={{ ...MInput.b, maxWidth: "140px" }}
          InputLabelProps={{
            shrink: true,
            sx: { color: MInput.a },
          }}
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          sx={{ ...MInput.b, maxWidth: "140px" }}
          InputLabelProps={{
            shrink: true,
            sx: { color: MInput.a },
          }}
        />
        <StyledButton onClick={fetchInvoices}>Get</StyledButton>
        <Autocomplete
          options={gstStatusOptions}
          value={gstStatus}
          onChange={(event, newValue) => {
            if (newValue) setGstStatus(newValue);
          }}
          PaperComponent={CustomPaper}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ ...MInput.b, maxWidth: "140px" }}
              InputLabelProps={{
                shrink: true,
                sx: { color: MInput.a },
              }}
              label="GST Status"
              size="small"
            />
          )}
          size="small"
          sx={{ width: 160 }}
        />
      </Stack>
      <StyledAgGridContainer
        component={Paper}
        className="ag-theme-alpine"
        sx={{ minHeight: "500px", mb: 2 }}
      >
        <AgGridReact
          rowData={filteredData}
          columnDefs={columnDefs}
          pinnedBottomRowData={footerData}
          suppressClipboardPaste={false}
          defaultColDef={{
            filter: true, // Enable filters by default
            sortable: true, // Optional: enable sorting
            resizable: true, // Optional: resizable columns
            floatingFilter: true, // Optional: show filter input below header
            flex: 1, // Make columns flexible
          }}
          pagination={true}
          paginationPageSize={100}
          animateRows={true}
          enableRangeSelection={false}
        />
      </StyledAgGridContainer>
      <ModernHeader label="DSA GST / TDS According to Invoiced Date" />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, mt: 2, alignItems: "center" }}
      >
        <TextField
          label="From Date"
          type="date"
          size="small"
          value={fromDateDsa}
          onChange={(e) => setFromDateDsa(e.target.value)}
          sx={{ ...MInput.b, maxWidth: "140px" }}
          InputLabelProps={{
            shrink: true,
            sx: { color: MInput.a },
          }}
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          value={toDateDsa}
          onChange={(e) => setToDateDsa(e.target.value)}
          sx={{ ...MInput.b, maxWidth: "140px" }}
          InputLabelProps={{
            shrink: true,
            sx: { color: MInput.a },
          }}
        />
        <StyledButton onClick={fetchDsaInvoices}>Get</StyledButton>
      </Stack>
      <StyledAgGridContainer
        component={Paper}
        className="ag-theme-alpine"
        sx={{ minHeight: "500px" }}
      >
        <AgGridReact
          rowData={filteredDsaData}
          columnDefs={columnDsaDefs}
          pinnedBottomRowData={footerDsaData}
          suppressClipboardPaste={false}
          getRowStyle={(params) => {
            if (params.data?.rowType === "Subtotal") {
              return {
                backgroundColor: "orange",
                fontWeight: "bold",
              };
            }
            return null;
          }}
          defaultColDef={{
            filter: true, // Enable filters by default
            sortable: false, // Optional: enable sorting
            resizable: true, // Optional: resizable columns
            floatingFilter: true, // Optional: show filter input below header
            flex: 1, // Make columns flexible
          }}
          pagination={true}
          paginationPageSize={100}
          animateRows={true}
          enableRangeSelection={false}
        />
      </StyledAgGridContainer>
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={async () => {
          await handleMarkGstPaid(selectedReceipt);
          setConfirmDialogOpen(false);
          setSelectedReceipt(null);
        }}
        title="Confirm GST Payment"
        content={
          <span>
            Are you sure you want to mark this receipt as <strong>GST Paid</strong>?
          </span>
        }
      />
      <ConfirmDialog
        open={confirmDsaDialogOpen}
        onClose={() => setConfirmDsaDialogOpen(false)}
        onConfirm={async () => {
          await handleMarkDsaGstPaid(selectedDsaReceipt);
          setConfirmDsaDialogOpen(false);
          setSelectedDsaReceipt(null);
        }}
        title="Confirm GST Payment"
        content={
          <span>
            Are you sure you want to mark this receipt as <strong>GST Paid</strong>?
          </span>
        }
      />
    </>
  );
};

GstTdsGrid.propTypes = {
  setAlert: PropTypes.func.isRequired,
  rowData: PropTypes.arrayOf(PropTypes.object), // if you're passing data to grid
  columnDefs: PropTypes.arrayOf(PropTypes.object), // grid column definitions
  setLoading: PropTypes.func, // if you're managing loading state
  loading: PropTypes.bool, // if you're managing loading state
};

export default GstTdsGrid;
